import { useState, useEffect } from 'react';
import { Pane, Card, Heading, Button, majorScale, Text, toaster } from 'evergreen-ui';
import { EditIcon, CircleArrowLeftIcon } from 'evergreen-ui';
import GuestsTable from './GuestsTable';
import GuestPeiComment from './GuestPeiComment';
import GuestNotes from './GuestNotes';
import AddGuestForm from './AddGuestForm';
import GuestExtraFieldsManager from './GuestExtraFieldsManager';
import { getGuests, setGuestActive } from '../services/guestsServices';

const Guests = ({ guests, setGuests, isDirector }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newGuest, setNewGuest] = useState({ nome: '', cognome: '', data_nascita: '', sesso: 'M', luogo_nascita: '', residenza: '', domicilio: '', motivazione: 'minori stranieri non accompagnati' });
    const [editingGuestIndex, setEditingGuestIndex] = useState(null);
    const [dragOverZone, setDragOverZone] = useState(null); // 'active' | 'ex' | null

    useEffect(() => {
        const fetchGuests = async () => {
            try {
                const guestsData = await getGuests();
                setGuests(guestsData);
            } catch (error) {
                console.error('Errore nel recupero degli ospiti:', error);
            }
        };
        fetchGuests();
    }, [setGuests]);

    const activeGuests = (guests || []).filter(g => Number(g.is_active) !== 0);
    const exGuests = (guests || []).filter(g => Number(g.is_active) === 0);

    const handleMoveToEx = async (guestId) => {
        try {
            await setGuestActive(guestId, false);
            setGuests(prev => prev.map(g => String(g.id) === String(guestId) ? { ...g, is_active: 0 } : g));
            toaster.success('Ospite spostato tra gli ex ospiti');
        } catch {
            toaster.danger("Errore nello spostamento dell'ospite");
        }
    };

    const handleMoveToActive = async (guestId) => {
        try {
            await setGuestActive(guestId, true);
            setGuests(prev => prev.map(g => String(g.id) === String(guestId) ? { ...g, is_active: 1 } : g));
            toaster.success('Ospite riportato tra gli ospiti attivi');
        } catch {
            toaster.danger("Errore nello spostamento dell'ospite");
        }
    };

    const handleDragStart = (e, guestId, fromActive) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ guestId: String(guestId), fromActive }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const makeDragZoneStyle = (zone) => ({
        border: dragOverZone === zone ? '2px dashed #429777' : '2px dashed transparent',
        borderRadius: 4,
        transition: 'border-color 0.15s',
        minHeight: 40,
    });

    const handleDropOnActive = async (e) => {
        e.preventDefault();
        setDragOverZone(null);
        try {
            const data = JSON.parse(e.dataTransfer.getData('text/plain'));
            if (!data.fromActive) {
                await handleMoveToActive(parseInt(data.guestId, 10));
            }
        } catch {}
    };

    const handleDropOnEx = async (e) => {
        e.preventDefault();
        setDragOverZone(null);
        try {
            const data = JSON.parse(e.dataTransfer.getData('text/plain'));
            if (data.fromActive) {
                await handleMoveToEx(parseInt(data.guestId, 10));
            }
        } catch {}
    };

    const handleExitEditMode = () => {
        setIsEditing(false);
        setEditingGuestIndex(null);
    };

    const exGuestsSection = (
        <Pane marginTop={majorScale(3)}>
            <Heading size={600} marginBottom={12}>Ex ospiti</Heading>
            <div
                style={makeDragZoneStyle('ex')}
                onDragOver={(e) => { e.preventDefault(); setDragOverZone('ex'); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverZone(null); }}
                onDrop={handleDropOnEx}
            >
                {exGuests.length === 0 ? (
                    <Text color="muted" size={400} display="block" paddingY={8}>
                        Nessun ex ospite. Trascina qui un ospite per spostarlo.
                    </Text>
                ) : (
                    <GuestsTable
                        guests={exGuests}
                        setGuests={setGuests}
                        editingGuestIndex={null}
                        setEditingGuestIndex={() => {}}
                        isEditing={false}
                        isDraggable={true}
                        onGuestDragStart={(e, guest) => handleDragStart(e, guest.id, false)}
                        onGuestDragEnd={() => setDragOverZone(null)}
                    />
                )}
            </div>
        </Pane>
    );

    if (!isEditing) {
        return (
            <Card elevation={2} padding={24} marginBottom={32} background="tint1">
                <Heading size={700} marginBottom={16}>Ospiti</Heading>
                <div
                    style={makeDragZoneStyle('active')}
                    onDragOver={(e) => { e.preventDefault(); setDragOverZone('active'); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverZone(null); }}
                    onDrop={handleDropOnActive}
                >
                    <GuestsTable
                        guests={activeGuests}
                        setGuests={setGuests}
                        editingGuestIndex={null}
                        setEditingGuestIndex={() => {}}
                        isEditing={false}
                        isDraggable={true}
                        onGuestDragStart={(e, guest) => handleDragStart(e, guest.id, true)}
                        onGuestDragEnd={() => setDragOverZone(null)}
                    />
                </div>
                {isDirector && (
                    <Button appearance="primary" intent="success" marginTop={16} onClick={() => setIsEditing(true)}>
                        Modifica <EditIcon marginLeft={8} />
                    </Button>
                )}
                {exGuestsSection}
                <GuestPeiComment guests={activeGuests} setGuests={setGuests} />
                <GuestNotes guests={activeGuests} isDirector={isDirector} />
            </Card>
        );
    }

    return (
        <Pane>
            <Card elevation={2} padding={24} marginBottom={32} background="tint1">
                <Heading size={700} marginBottom={16}>Gestione ospiti</Heading>
                <div
                    style={makeDragZoneStyle('active')}
                    onDragOver={(e) => { e.preventDefault(); setDragOverZone('active'); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverZone(null); }}
                    onDrop={handleDropOnActive}
                >
                    <GuestsTable
                        guests={activeGuests}
                        setGuests={setGuests}
                        editingGuestIndex={editingGuestIndex}
                        setEditingGuestIndex={setEditingGuestIndex}
                        isEditing={true}
                        isDirector={isDirector}
                        isDraggable={true}
                        onGuestDragStart={(e, guest) => handleDragStart(e, guest.id, true)}
                        onGuestDragEnd={() => setDragOverZone(null)}
                    />
                </div>
                <GuestExtraFieldsManager />
                <AddGuestForm
                    newGuest={newGuest}
                    setGuests={setGuests}
                    setNewGuest={setNewGuest}
                />
                {exGuestsSection}
                <Button appearance="primary" intent="success" marginTop={majorScale(2)} onClick={handleExitEditMode} width="9.1rem">
                    Torna indietro <CircleArrowLeftIcon marginLeft={8} />
                </Button>
            </Card>
        </Pane>
    );
};

export default Guests;
